import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import * as api from 'lib/api';
import API_RESOURCES from 'lib/api/resources';
import './UserClientRoles.scss';

const UserClientRoles = ({ userId }) => {
  const [assignments, setAssignments] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [assignmentToRemove, setAssignmentToRemove] = useState(null);
  const [clientLocked, setClientLocked] = useState(false);
  
  const authObject = useSelector(state => state.authReducer?.authObject);
  const isAdmin = authObject?.roles?.includes('admin');

  useEffect(() => {
    if (userId) {
      loadAssignments();
      loadAllClients();
      loadAvailableRoles();
    }
  }, [userId]);

  const loadAssignments = async () => {
    try {
      setLoading(true);
      const response = await api.get(`${API_RESOURCES.USER_CLIENT_ROLES}/by-user/${userId}`);
      setAssignments(response.data?.assignments || []);
    } catch (error) {
      console.error('Error loading assignments:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllClients = async () => {
    try {
      const response = await api.get(API_RESOURCES.CLIENTS);
      setAllClients(response.data || []);
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  const loadAvailableRoles = async () => {
    try {
      const response = await api.get(`${API_RESOURCES.USER_CLIENT_ROLES}/available-roles`);
      setAvailableRoles(response.data || []);
    } catch (error) {
      console.error('Error loading roles:', error);
    }
  };

  const handleAddAssignment = async () => {
    if (!selectedClient || !selectedRole) {
      alert('Please select a client and role');
      return;
    }

    try {
      await api.post(API_RESOURCES.USER_CLIENT_ROLES, {
        user: userId,
        client: selectedClient,
        role: selectedRole
      });

      setShowAddModal(false);
      setSelectedClient('');
      setSelectedRole('');
      setClientLocked(false);
      loadAssignments();
    } catch (error) {
      console.error('Error adding assignment:', error);
      alert(error.message || 'Error adding assignment');
    }
  };

  const handleRemoveClick = (assignmentId) => {
    setAssignmentToRemove(assignmentId);
    setShowConfirmModal(true);
  };

  const handleConfirmRemove = async () => {
    if (!assignmentToRemove) return;

    try {
      await api.deleteMethod(`${API_RESOURCES.USER_CLIENT_ROLES}/${assignmentToRemove}`);
      loadAssignments();
      setShowConfirmModal(false);
      setAssignmentToRemove(null);
    } catch (error) {
      console.error('Error removing assignment:', error);
      alert(error.message || 'Error removing assignment');
      setShowConfirmModal(false);
      setAssignmentToRemove(null);
    }
  };

  const handleChangeRole = async (assignmentId, newRole) => {
    try {
      await api.deleteMethod(`${API_RESOURCES.USER_CLIENT_ROLES}/${assignmentId}`);

      const assignment = assignments.find(a => a._id === assignmentId);
      await api.post(API_RESOURCES.USER_CLIENT_ROLES, {
        user: userId,
        client: assignment.client._id,
        role: newRole
      });

      loadAssignments();
    } catch (error) {
      console.error('Error changing role:', error);
      alert(error.message || 'Error changing role');
    }
  };

  const getClientAssignments = (clientId) => {
    return assignments.filter(a => a.client._id === clientId);
  };

  const getSelectedClientForModal = () => {
    return allClients.find(c => c._id === selectedClient);
  };

  const activeClients = allClients.filter(c => c.status === 'active');

  if (loading) {
    return <div className="user-client-roles loading">Loading...</div>;
  }

  return (
    <div className="user-client-roles">
      <div className="header">
        <h3>Client Role Assignments</h3>
      </div>

      <div className="assignments-list">
        {activeClients.length === 0 ? (
          <div className="empty-state">
            No active clients available.
          </div>
        ) : (
          activeClients.map(client => {
            const clientAssignments = getClientAssignments(client._id);
            return (
              <div key={client._id} className="client-item">
                <div className="client-info">
                  <div className="client-name">{client.name}</div>
                  <div className="client-code">{client.code}</div>
                </div>
                
                <div className="roles-list">
                  {clientAssignments.length === 0 ? (
                    <span className="no-roles">No roles assigned</span>
                  ) : (
                    clientAssignments.map(assignment => (
                      <div key={assignment._id} className="role-item">
                        {isAdmin ? (
                          <select
                            value={assignment.role._id || assignment.role}
                            onChange={(e) => handleChangeRole(assignment._id, e.target.value)}
                            className="form-control form-control-sm"
                          >
                            {availableRoles.filter(role => role._id !== 'admin').map(role => (
                              <option key={role._id} value={role._id}>
                                {role.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="role-badge">{assignment.role.name || assignment.role}</span>
                        )}
                        {isAdmin && (
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleRemoveClick(assignment._id)}
                            title="Remove role"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))
                  )}
                  {isAdmin && clientAssignments.length > 0 && (
                    <button
                      className="btn btn-success btn-sm"
                      onClick={() => {
                        setSelectedClient(client._id);
                        setSelectedRole('');
                        setClientLocked(true);
                        setShowAddModal(true);
                      }}
                      style={{ marginTop: '8px' }}
                    >
                      + Add Role
                    </button>
                  )}
                </div>

                {isAdmin && clientAssignments.length === 0 && (
                  <button
                    className="btn btn-success btn-sm"
                    onClick={() => {
                      setSelectedClient(client._id);
                      setSelectedRole('');
                      setClientLocked(true);
                      setShowAddModal(true);
                    }}
                  >
                    Assign Role
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      {showAddModal && (
        <div className="modal-overlay" onClick={() => {
          setShowAddModal(false);
          setSelectedClient('');
          setSelectedRole('');
          setClientLocked(false);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h4>Add Client Role Assignment</h4>
              <button 
                className="close-btn"
                onClick={() => {
                  setShowAddModal(false);
                  setSelectedClient('');
                  setSelectedRole('');
                  setClientLocked(false);
                }}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Client</label>
                <select
                  value={selectedClient}
                  onChange={(e) => {
                    setSelectedClient(e.target.value);
                    setSelectedRole('');
                  }}
                  disabled={clientLocked}
                  className="form-control"
                >
                  <option value="">-- Select Client --</option>
                  {activeClients.map(client => (
                    <option key={client._id} value={client._id}>
                      {client.code} - {client.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Role</label>
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="form-control"
                >
                  <option value="">Select a role</option>
                  {selectedClient && availableRoles
                    .filter(role => {
                      if (role._id === 'admin') return false;
                      const clientAssignments = getClientAssignments(selectedClient);
                      return !clientAssignments.some(a => a.role._id === role._id || a.role === role._id);
                    })
                    .map(role => (
                      <option key={role._id} value={role._id}>
                        {role.name}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowAddModal(false);
                  setSelectedClient('');
                  setSelectedRole('');
                  setClientLocked(false);
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddAssignment}
                disabled={!selectedClient || !selectedRole}
              >
                Add Assignment
              </button>
            </div>
          </div>
        </div>
      )}

      {showConfirmModal && (
        <div className="modal-overlay" onClick={() => setShowConfirmModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h4>Confirm Removal</h4>
              <button 
                className="close-btn"
                onClick={() => {
                  setShowConfirmModal(false);
                  setAssignmentToRemove(null);
                }}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <p>Are you sure you want to remove this role assignment?</p>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowConfirmModal(false);
                  setAssignmentToRemove(null);
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleConfirmRemove}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserClientRoles;
